import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@avg/shared";
import { api } from "../api";
import { useT } from "../i18n/I18nContext";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogleIdToken: (idToken: string) => Promise<void>;
  signInWithDev: (email: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.me();
      setUser(user);
    } catch {
      // Either no session cookie, expired, or revoked. Either way: anonymous.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signInWithGoogleIdToken = useCallback(
    async (idToken: string) => {
      setError(null);
      try {
        const { user } = await api.loginWithGoogle(idToken);
        setUser(user);
      } catch (err: any) {
        setError(err?.message ?? t("login.signInFailed"));
        throw err;
      }
    },
    [t],
  );

  const signInWithDev = useCallback(
    async (email: string, name?: string) => {
      setError(null);
      try {
        const { user } = await api.devLogin(email, name);
        setUser(user);
      } catch (err: any) {
        setError(err?.message ?? t("login.signInFailed"));
        throw err;
      }
    },
    [t],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Even if the server call fails (network, expired session) we still
      // want to clear local state.
    }
    setUser(null);
    try {
      (window as any).google?.accounts?.id?.disableAutoSelect?.();
    } catch {
      // Ignore — Google library may not be loaded.
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      signInWithGoogleIdToken,
      signInWithDev,
      signOut,
      refresh,
    }),
    [user, loading, error, signInWithGoogleIdToken, signInWithDev, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
